import React, { useEffect, useRef, useState } from 'react';
import { api, request } from '../../api/client';
import { Button, Field, Notice } from '../../design-system/components';
import { money } from './helpers';
import type { Bootstrap, PreviewResponse, Proposal } from '../../types/contracts';

type ModalKind = 'emit' | 'emit-download' | 'revision' | null;

function download(url: string, name: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function FinalizeProposal({
  id,
  onBack,
  onManage,
  onRevision,
}: {
  id: string;
  onBack: () => void;
  onManage: () => void;
  onRevision: (id: string) => void;
}) {
  const [data, setData] = useState<PreviewResponse | null>(null);
  const [p, setP] = useState<Proposal | null>(null);
  const [boot, setBoot] = useState<Bootstrap | null>(null);
  const [modal, setModal] = useState<ModalKind>(null);
  const [revisionReason, setRevisionReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const key = useRef(crypto.randomUUID());
  async function load() {
    try {
      const proposal = await api.get<Proposal>(`/proposals/${id}`);
      setP(proposal);
      setBoot(await api.get('/bootstrap'));
      if (!proposal.immutable) setData(await api.get(`/proposals/${id}/preview`));
    } catch (e: any) {
      setError(e.message);
    }
  }
  useEffect(() => {
    load();
  }, [id]);
  const url = api.fileUrl(`/proposals/${id}/issued-pdf`);
  async function emit(downloadAfter: boolean) {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const r = await request<any>(`/proposals/${id}/emit`, {
        method: 'POST',
        headers: { 'Idempotency-Key': key.current },
        body: JSON.stringify({ download_after: downloadAfter, idempotency_key: key.current }),
      });
      setModal(null);
      await load();
      if (downloadAfter) download(url, r.name || `ZURC_PROPOSTA_${p?.code}.pdf`);
    } catch (e: any) {
      setError(e.message);
      setModal(null);
    } finally {
      setBusy(false);
    }
  }
  async function createRevision(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const reason = revisionReason.trim();
    if (reason.length < 3) {
      setError('Informe o motivo da nova revisão.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await api.post<{ id: string }>(`/proposals/${id}/revisions`, { reason });
      setModal(null);
      setRevisionReason('');
      onRevision(r.id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  if (!p) return <main className="page">Carregando finalização...</main>;
  const modalOpen = Boolean(modal);
  return (
    <div className="preview-shell">
      <header className="preview-toolbar" aria-hidden={modalOpen || undefined} inert={modalOpen ? true : undefined}>
        <div>
          <span className="eyebrow">FINALIZAR PROPOSTA</span>
          <h1>
            {p.code} · REV {String(p.revision).padStart(2, '0')}
          </h1>
          <p>
            {p.immutable
              ? 'Esta revisão foi emitida e está preservada.'
              : 'Confira o documento antes da emissão. Depois de emitida, esta revisão ficará preservada e não poderá ser alterada.'}
          </p>
        </div>
        <div className="toolbar-actions">
          {!p.immutable && (
            <Button variant="secondary" onClick={onBack}>
              Voltar e editar
            </Button>
          )}
          {p.immutable ? (
            <>
              <Button variant="secondary" onClick={onManage}>
                Gerenciar proposta
              </Button>
              <Button variant="secondary" onClick={() => setModal('revision')}>
                Criar nova revisão
              </Button>
              <a className="btn btn-primary" href={url} download>
                Baixar PDF
              </a>
            </>
          ) : (
            <>
              <Button onClick={() => setModal('emit')} disabled={busy || !boot?.pdf_engine.available}>
                Emitir
              </Button>
              <Button onClick={() => setModal('emit-download')} disabled={busy || !boot?.pdf_engine.available}>
                Emitir e baixar
              </Button>
            </>
          )}
        </div>
      </header>
      {error && (
        <Notice tone="danger" title="Ação impedida">
          {error}
        </Notice>
      )}
      {!boot?.pdf_engine.available && (
        <Notice tone="warning" title="Emissão indisponível">
          {boot?.pdf_engine.message}
        </Notice>
      )}
      {data?.blocking_placeholders?.length ? (
        <Notice tone="warning" title="Dados institucionais pendentes">
          Confirme os dados institucionais antes de emitir: {data.blocking_placeholders.join(', ')}.
        </Notice>
      ) : null}
      {Boolean(p.immutable) && (
        <Notice tone="success" title="PDF emitido e preservado">
          SHA-256: <code>{p.last_pdf_hash}</code>
        </Notice>
      )}
      <div className="a4-preview">
        <iframe
          title={p.immutable ? 'PDF emitido' : 'Pré-visualização da proposta'}
          src={p.immutable ? url : undefined}
          srcDoc={p.immutable ? undefined : data?.html || '<p>Carregando...</p>'}
        />
      </div>
      {(modal === 'emit' || modal === 'emit-download') && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="emit-title">
          <div className="modal emission-modal">
            <span className="eyebrow">CONFIRMAR EMISSÃO</span>
            <h2 id="emit-title">Você está prestes a emitir:</h2>
            <div className="emission-reference">
              {p.code} · REV {String(p.revision).padStart(2, '0')}
            </div>
            <p>
              Após a emissão, esta revisão ficará imutável.
              <br />
              Alterações posteriores exigirão uma nova revisão.
            </p>
            <dl>
              <dt>Cliente</dt>
              <dd>{p.client_legal_name || p.client_name}</dd>
              <dt>Serviço</dt>
              <dd>{p.service_code}</dd>
              <dt>Preço comercial</dt>
              <dd>{money(p.pricing_json?.commercial_price ?? p.pricing_json?.final_price)}</dd>
              <dt>Validade</dt>
              <dd>{p.payment_json?.validity_days ? `${p.payment_json.validity_days} dias` : '—'}</dd>
            </dl>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setModal(null)} disabled={busy}>
                Cancelar
              </Button>
              <Button onClick={() => emit(modal === 'emit-download')} disabled={busy}>
                {busy ? 'Emitindo...' : modal === 'emit-download' ? 'Emitir e baixar' : 'Emitir'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {modal === 'revision' && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="revision-title">
          <form className="modal" onSubmit={createRevision}>
            <span className="eyebrow">NOVA REVISÃO</span>
            <h2 id="revision-title">Criar revisão editável</h2>
            <p>A revisão emitida permanecerá preservada. A nova revisão será vinculada e aberta como rascunho.</p>
            <Field id="revision-reason" label="Motivo" required>
              <textarea id="revision-reason" value={revisionReason} onChange={(e) => setRevisionReason(e.target.value)} required />
            </Field>
            <div className="modal-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setModal(null);
                  setRevisionReason('');
                }}
                disabled={busy}
              >
                Cancelar
              </Button>
              <Button disabled={busy}>{busy ? 'Criando...' : 'Criar nova revisão'}</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
